import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { GetUsers } from '../components/mainUsers/users/users';
import Search from '../components/search/search';
import { fetchingUserStart, fetchingUserReset } from '../redux/user/userSlice';
import { fetchingGroupReset } from '../redux/group/groupSlice';
import { fetchingApplicationReset } from '../redux/application/applicationSlice';
import { fetchingDeviceReset } from '../redux/device/deviceSlice';
import { fetchingDelegationReset } from '../redux/delegation/delegationSlice';
import { fetchingOrganizationReset } from '../redux/organization/organizationSlice';

/* ═══════════════════════════════════════════════════════════════════
   Page Utilisateurs : l'URL et la fiche affichée disent la même chose.

   Deux effets, dans les deux sens, et rien d'autre :
     · le paramètre `user` commande le chargement — arrivée par lien depuis la
       fiche appareil, signet, rechargement, retour arrière ;
     · la fiche chargée commande le paramètre — recherche tapée ou choisie dans
       les suggestions.

   Sans le second, une recherche laissait l'URL nue : la fiche n'était ni
   partageable ni rechargeable, et F5 renvoyait une page vide.
   ═══════════════════════════════════════════════════════════════════ */

// Un UPN saisi à la main ne respecte pas forcément la casse du répertoire :
// la comparaison des deux sens doit être insensible, sinon les deux effets se
// relancent l'un l'autre indéfiniment.
function memeCompte(a, b) {
    if (!a || !b) return false;
    return String(a).toLowerCase() === String(b).toLowerCase();
}

export default function Users() {
    const inputRef = useRef(null);
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentUser, isActive, isError, isLoading } = useSelector((state) => state.user);

    const upnDemande = searchParams.get('user');

    const demandeEnCours = useRef(null);
    const appelLance = useRef(false);

    // ── Sens 1 : le paramètre charge la fiche ──
    //
    // On remplit le champ de recherche — il reste la source de vérité de
    // l'appel — puis on rejoue la séquence de la loupe : remise à zéro des six
    // tranches, et démarrage au tick suivant. Le délai n'est pas décoratif :
    // React 18 regroupe les dispatches d'un même tick, `isActive` passerait de
    // false à true dans le même rendu, la fiche ne serait pas démontée et
    // garderait les appareils et les groupes du compte précédent.
    useEffect(() => {
        if (!upnDemande) {
            demandeEnCours.current = null;
            return undefined;
        }
        // Déjà à l'écran : c'est le sens 2 qui vient d'écrire ce paramètre, le
        // recharger ferait un aller-retour réseau pour rien.
        if (memeCompte(currentUser?.userPrincipalName, upnDemande)) {
            demandeEnCours.current = null;
            return undefined;
        }

        demandeEnCours.current = upnDemande;
        appelLance.current = false;

        if (inputRef.current) inputRef.current.value = upnDemande;
        dispatch(fetchingUserReset());
        dispatch(fetchingGroupReset());
        dispatch(fetchingApplicationReset());
        dispatch(fetchingDeviceReset());
        dispatch(fetchingDelegationReset());
        dispatch(fetchingOrganizationReset());

        const depart = setTimeout(() => dispatch(fetchingUserStart()), 100);
        return () => clearTimeout(depart);
        // currentUser volontairement hors des dépendances : il change à chaque
        // chargement et ne doit pas relancer cet effet, il ne sert qu'à savoir
        // si le travail est déjà fait.
    }, [upnDemande, dispatch]);

    // ── Sens 2 : la fiche chargée écrit le paramètre ──
    //
    // En `replace` : l'URL est le reflet de ce qui est affiché, pas une
    // navigation. Sans ça, chaque recherche empilerait une entrée d'historique
    // et le bouton Retour ne ramènerait plus à la fiche appareil d'où l'on
    // vient. L'UPN écrit est celui du répertoire, pas celui qui a été tapé.
    useEffect(() => {
        const upnCharge = currentUser?.userPrincipalName;

        if (isLoading) {
            appelLance.current = true;
        } else if (appelLance.current) {
            appelLance.current = false;
            demandeEnCours.current = null;
        }

        // Un chargement demandé par l'URL est en cours : l'URL dit ce qu'on veut
        // voir, le store dit encore ce qu'on voyait. Ne rien écrire.
        if (demandeEnCours.current) return;

        // Comparaison STRICTE ici, insensible à la casse dans l'autre sens : si
        // le paramètre ne diffère que par la casse, il faut quand même le
        // réécrire. La recherche en base est sensible à la casse, une URL portant
        // « FLORIAN.PRAT@… » serait un signet mort.
        if (upnCharge && upnCharge !== upnDemande) {
            setSearchParams((precedents) => {
                const suivants = new URLSearchParams(precedents);
                suivants.set('user', upnCharge);
                return suivants;
            }, { replace: true });
            return;
        }

        // Recherche en échec — compte inconnu ou hors périmètre : le paramètre
        // resté en place ferait recharger le compte précédent au prochain F5.
        if (!upnCharge && isError && upnDemande) {
            setSearchParams((precedents) => {
                const suivants = new URLSearchParams(precedents);
                suivants.delete('user');
                return suivants;
            }, { replace: true });
        }
    }, [currentUser, isError, isLoading, upnDemande, setSearchParams]);

    const isCurrentSearchUsers = () => {
        if (currentUser) {
            return null;
        } else {
            return inputRef.current.value;
        }
    }

    return (
        <>
            <Search inputRef={inputRef} inputPlaceHolder={"Rentrez l'adresse mail d'un utilisateur"} page="Users" needFilters={false} filterRow={null} setFilterRow={null} isActiveDiffSearch={null} setIsActiveDiffSearch={null}/>
            {isActive ? (
                <GetUsers usernameInput={isCurrentSearchUsers()}/>
            ) : (
                ""
            )}
        </>
    )
}
